// useRefreshAll — subscribe a component to the dashboard-wide "Refresh
// now" button. The button (rendered inside LiveChart's toolbar) fires a
// CustomEvent on window; every chart that calls this hook gets a
// monotonically-increasing nonce that flips on each fire so callers can
// add it to their useEffect dependency list to trigger a refetch.
//
// Why a window event instead of a Context provider:
//   - zero plumbing for components deep in the tree
//   - works for components that aren't wrapped in any provider yet
//   - cleanup is just removeEventListener; no provider value churn.
import { useEffect, useState } from "react";

export const REFRESH_ALL_EVENT = "sflow:refresh-all";

/**
 * Returns a number that increments every time the global "Refresh All"
 * event fires. Add the returned value to your useEffect dep list to
 * trigger a refetch in lockstep with every other chart.
 */
export function useRefreshAll(): number {
  const [nonce, setNonce] = useState(0);
  useEffect(() => {
    const handler = () => setNonce((n) => n + 1);
    window.addEventListener(REFRESH_ALL_EVENT, handler);
    return () => window.removeEventListener(REFRESH_ALL_EVENT, handler);
  }, []);
  return nonce;
}

/** Imperatively trigger a refresh of every subscribed chart. */
export function fireRefreshAll(): void {
  window.dispatchEvent(new CustomEvent(REFRESH_ALL_EVENT));
}
